import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { useItems, type ItemListQuery } from '@/hooks/use-items';
import { useCategoriesFlat } from '@/hooks/use-categories';
import { useT } from '@/i18n/use-t';

const PAGE_SIZE = 25;

export function ItemsListPage() {
  const t = useT();
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [status, setStatus] = useState<'active' | 'archived'>('active');
  const [page, setPage] = useState(1);

  const query: ItemListQuery = {
    page,
    pageSize: PAGE_SIZE,
    status,
    categoryId: categoryId === '' ? undefined : categoryId,
    search: search.trim() === '' ? undefined : search.trim(),
  };
  const { data, isLoading, isFetching } = useItems(query);
  const cats = useCategoriesFlat();

  const totalPages = data === undefined ? 1 : Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('catalog.items.title', 'Catalog')}</h1>
          <p className="text-sm text-muted-foreground">
            {data === undefined
              ? '…'
              : t('catalog.items.count', '{count} items').replace(
                  '{count}',
                  String(data.total),
                )}{' '}
            {isFetching ? t('catalog.items.refreshing', '· refreshing…') : ''}
          </p>
        </div>
        <Button asChild>
          <Link to="/catalog/items/new">{t('catalog.items.new', 'New item')}</Link>
        </Button>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[280px] flex-1">
          <Input
            placeholder={t('catalog.items.searchPlaceholder', 'Search name, SKU, or scan barcode…')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="min-w-[200px]">
          <Select
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">{t('catalog.items.allCategories', 'All categories')}</option>
            {cats.flat.map((c) => (
              <option key={c.id} value={c.id}>
                {'— '.repeat(c.depth)}
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="min-w-[140px]">
          <Select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as 'active' | 'archived');
              setPage(1);
            }}
          >
            <option value="active">{t('catalog.items.statusActive', 'Active')}</option>
            <option value="archived">{t('catalog.items.statusArchived', 'Archived')}</option>
          </Select>
        </div>
      </div>

      <div className="rounded-md border bg-background">
        <Table>
          <THead>
            <TR>
              <TH>SKU</TH>
              <TH>{t('catalog.common.name', 'Name')}</TH>
              <TH>{t('catalog.common.category', 'Category')}</TH>
              <TH>{t('catalog.common.barcode', 'Barcode')}</TH>
              <TH>{t('catalog.common.status', 'Status')}</TH>
            </TR>
          </THead>
          <TBody>
            {isLoading && data === undefined && (
              <TR>
                <TD colSpan={5} className="py-8 text-center text-muted-foreground">
                  {t('common.loading', 'Loading…')}
                </TD>
              </TR>
            )}
            {data !== undefined && data.items.length === 0 && (
              <TR>
                <TD colSpan={5} className="py-8 text-center text-muted-foreground">
                  {t('catalog.items.empty', 'No items match these filters.')}
                </TD>
              </TR>
            )}
            {data?.items.map((row) => (
              <TR key={row.id}>
                <TD className="font-mono text-xs">
                  <Link to={`/catalog/items/${row.id}`} className="hover:underline">
                    {row.sku}
                  </Link>
                </TD>
                <TD>
                  <Link to={`/catalog/items/${row.id}`} className="font-medium hover:underline">
                    {row.name}
                  </Link>
                </TD>
                <TD className="text-sm text-muted-foreground">{row.categoryName}</TD>
                <TD className="font-mono text-xs">{row.primaryBarcode ?? '—'}</TD>
                <TD>
                  <Badge variant={row.status === 'active' ? 'success' : 'outline'}>
                    {row.status}
                  </Badge>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {t('catalog.items.page', 'Page {page} of {pages}')
            .replace('{page}', String(data?.page ?? page))
            .replace('{pages}', String(totalPages))}
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            {t('catalog.items.prev', 'Prev')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('catalog.items.next', 'Next')}
          </Button>
        </div>
      </div>
    </div>
  );
}
